/**
 * Exposure Alerts configuration card — summary view.
 * Reads from the unified exposure_settings setting.
 */

import { useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useMy } from '@/hooks/useMy';
import { EXPOSURE_CATEGORIES } from '@/sections/settings/components/cards/exposureAlertCategories';
import { CategoryExposureAlertsForm } from '@/sections/settings/components/forms/CategoryExposureAlertsForm';
import { SettingEditableCard } from '@/sections/settings/components/SettingEditableCard';
import { useAuth } from '@/state/auth';
import { Setting } from '@/types';

interface ExposureSettings {
  risk_exposures: Record<string, boolean>;
  port_categories: Record<string, boolean>;
  custom_ports: Record<string, boolean>;
  custom_protocols: Record<string, boolean>;
}

const RISK_DISPLAY_NAMES: Record<string, string> = {
  'web-login-sso': 'Web Logins (SSO)',
  'web-login-generic': 'Web Logins (Generic)',
  'llm-chatbot': 'LLM Chatbots',
};

interface ExposureAlertsCardProps {
  customerEmail?: string;
  /**
   * Whether edit functionality is enabled (default: true)
   */
  editable?: boolean;
}

/**
 * Exposure Alerts card with view mode showing current configuration.
 * Edit button opens modal with full CategoryExposureAlertsForm.
 */
export function ExposureAlertsCard({
  customerEmail,
  editable = true,
}: ExposureAlertsCardProps) {
  const { me, friend } = useAuth();
  const effectiveEmail = customerEmail || friend || me;

  const { data: settings, status: settingsStatus } = useMy(
    { resource: 'setting' },
    { email: effectiveEmail }
  );

  const summary = useMemo(() => {
    const raw = settings?.find((s: Setting) => s.name === 'exposure_settings');
    const es: ExposureSettings = raw?.value
      ? (raw.value as ExposureSettings)
      : {
          risk_exposures: {},
          port_categories: {},
          custom_ports: {},
          custom_protocols: {},
        };

    const riskEnabled = Object.keys(es.risk_exposures || {}).map(
      k => RISK_DISPLAY_NAMES[k] || k
    );
    const riskNotifyCount = Object.values(es.risk_exposures || {}).filter(
      Boolean
    ).length;

    const categoryIds = Object.keys(es.port_categories || {});
    const categoryNames = categoryIds
      .map(id => EXPOSURE_CATEGORIES.find(c => c.id === id)?.name)
      .filter(Boolean) as string[];

    const customCount =
      Object.keys(es.custom_ports || {}).length +
      Object.keys(es.custom_protocols || {}).length;

    const rawSevMap = settings?.find(
      (s: Setting) => s.name === 'finding_severity_map'
    );
    const sevMap = rawSevMap?.value as
      | { exposure_groups?: Record<string, string> }
      | undefined;
    const severityOverrideCount = Object.keys(
      sevMap?.exposure_groups || {}
    ).length;

    return {
      riskEnabled,
      riskNotifyCount,
      categoryNames,
      customCount,
      severityOverrideCount,
    };
  }, [settings]);

  const isLoading = settingsStatus === 'pending';

  const formatList = (items: string[], maxShow = 3): string => {
    if (items.length === 0) return 'None';
    if (items.length <= maxShow) return items.join(', ');
    return `${items.slice(0, maxShow).join(', ')}, +${items.length - maxShow} more`;
  };

  return (
    <section
      aria-labelledby="exposure-alerts-heading"
      data-testid="exposure-alerts-section"
      data-tutorial="exposure-alerts"
    >
      <SettingEditableCard
        data-testid="exposure-alerts-card"
        editable={editable}
        editConfig={{
          modalTitle: 'Exposure Alert Settings',
          formComponent: CategoryExposureAlertsForm,
          formProps: { customerEmail },
          modalSize: '5xl',
        }}
      >
        {isLoading ? (
          <Skeleton className="mx-auto flex justify-center py-4" />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h4
                id="exposure-alerts-heading"
                className="text-lg font-semibold"
              >
                Exposure Alerts
              </h4>
              <p className="text-chariot-text-secondary text-sm">
                Get notified when exposures are detected
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-chariot-text-secondary">
                Risk Exposures:
              </span>
              <span className="text-chariot-text-primary">
                {summary.riskEnabled.length > 0
                  ? `${formatList(summary.riskEnabled)} (${summary.riskNotifyCount} with notifications)`
                  : 'None'}
              </span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-chariot-text-secondary">
                Port Categories:
              </span>
              <span className="text-chariot-text-primary">
                {formatList(summary.categoryNames)}
              </span>
            </div>

            {summary.customCount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-chariot-text-secondary">Custom:</span>
                <span className="text-chariot-text-primary">
                  {summary.customCount} item
                  {summary.customCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {summary.severityOverrideCount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-chariot-text-secondary">
                  Severity Overrides:
                </span>
                <span className="text-chariot-text-primary">
                  {summary.severityOverrideCount} configured
                </span>
              </div>
            )}
          </div>
        )}
      </SettingEditableCard>
    </section>
  );
}
